import axiosInstance from 'api/axiosConfig'
import { ENDPOINTS } from 'api/endpoints'
import { getToken } from 'api/utils/AuthManager'
import {
  clearListingOwnerIntent,
  hasListingOwnerIntent,
  subscribeListingOwnerIntent,
} from 'api/utils/pendingCreateAdFlow'
import { useAuth } from 'pages/authenticated/AuthContext'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'

export type AppRole = 'owner' | 'tenant' | 'support' | 'admin'

type PermissionsPayload = {
  userId: string
  role: AppRole
  permissions: string[]
  email?: string | null
  emailValidated?: boolean
  hasListing?: boolean
}

type RefreshOptions = {
  intent?: 'list_property' | 'owner' | 'create_ad'
}

type PermissionsContextType = {
  role: AppRole | null
  permissions: string[]
  email: string | null
  emailValidated: boolean
  hasListing: boolean
  loading: boolean
  refresh: (options?: RefreshOptions) => Promise<PermissionsPayload | null>
  can: (permission: string) => boolean
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(
  undefined
)

function subscribeIntent(onStoreChange: () => void) {
  return subscribeListingOwnerIntent(onStoreChange)
}

function getIntentSnapshot() {
  return hasListingOwnerIntent()
}

async function loadPermissions(
  options?: RefreshOptions
): Promise<PermissionsPayload | null> {
  const token = getToken()
  if (!token) return null

  // Always GET — Hostinger Allow-Headers is only Authorization, Content-Type.
  // Custom headers / POST+JSON preflight with X-EasyBail-Intent → browser CORS block.
  const intent =
    options?.intent ||
    (hasListingOwnerIntent() ? 'list_property' : undefined)

  const res = await axiosInstance.get<{
    success: boolean
    data?: PermissionsPayload
  }>(ENDPOINTS.AUTH.ME_PERMISSIONS, {
    headers: { Authorization: `Bearer ${token}` },
    params: intent ? { intent } : undefined,
  })

  if (!res.data?.success || !res.data.data) return null
  return res.data.data
}

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth()
  const ownerIntentActive = useSyncExternalStore(
    subscribeIntent,
    getIntentSnapshot,
    () => false
  )
  const [role, setRole] = useState<AppRole | null>(null)
  const [permissions, setPermissions] = useState<string[]>([])
  const [email, setEmail] = useState<string | null>(null)
  const [emailValidated, setEmailValidated] = useState(false)
  const [hasListing, setHasListing] = useState(false)
  const [loading, setLoading] = useState(true)
  const requestSeq = useRef(0)
  const isAuthenticatedRef = useRef(isAuthenticated)
  isAuthenticatedRef.current = isAuthenticated

  const refresh = useCallback(
    async (options?: RefreshOptions): Promise<PermissionsPayload | null> => {
      const seq = ++requestSeq.current
      const authed = isAuthenticatedRef.current

      if (!authed) {
        if (seq !== requestSeq.current) return null
        clearListingOwnerIntent()
        setRole(null)
        setPermissions([])
        setEmail(null)
        setEmailValidated(false)
        setHasListing(false)
        setLoading(false)
        return null
      }

      const ownerIntent =
        hasListingOwnerIntent() ||
        options?.intent === 'list_property' ||
        options?.intent === 'owner' ||
        options?.intent === 'create_ad'

      if (ownerIntent) {
        setRole(prev =>
          prev === 'admin' || prev === 'support' ? prev : 'owner'
        )
      }

      setLoading(true)
      try {
        const payload = await loadPermissions(options)
        if (seq !== requestSeq.current) return payload

        const listingOwned = Boolean(payload?.hasListing)
        let nextRole = payload?.role ?? null

        if (
          (hasListingOwnerIntent() || listingOwned) &&
          nextRole === 'tenant'
        ) {
          nextRole = 'owner'
        }

        setRole(nextRole)
        setPermissions(
          Array.isArray(payload?.permissions) ? payload!.permissions : []
        )
        setEmail(typeof payload?.email === 'string' ? payload.email : null)
        setEmailValidated(Boolean(payload?.emailValidated))
        setHasListing(listingOwned)

        // Only drop client owner-intent once the API confirms owner (or listing).
        if (
          hasListingOwnerIntent() &&
          (nextRole === 'owner' || listingOwned)
        ) {
          // Keep intent until email gate is satisfied so menu + /account stay owner-aware.
          if (payload?.emailValidated) {
            clearListingOwnerIntent()
          }
        }

        return payload
          ? {
              ...payload,
              role: (nextRole ?? payload.role) as AppRole,
              hasListing: listingOwned,
            }
          : null
      } catch {
        if (seq !== requestSeq.current) return null
        if (!hasListingOwnerIntent()) {
          setRole(null)
          setPermissions([])
          setEmail(null)
          setEmailValidated(false)
          setHasListing(false)
        }
        return null
      } finally {
        if (seq === requestSeq.current) {
          setLoading(false)
        }
      }
    },
    []
  )

  useEffect(() => {
    void refresh(
      hasListingOwnerIntent() ? { intent: 'list_property' } : undefined
    )
  }, [isAuthenticated, refresh])

  const can = useCallback(
    (permission: string) =>
      permissions.includes('*') || permissions.includes(permission),
    [permissions]
  )

  const displayRole: AppRole | null = (() => {
    if (!isAuthenticated) return null
    if (role === 'admin' || role === 'support') return role
    if (ownerIntentActive || hasListing) return 'owner'
    return role
  })()

  const value = useMemo(
    () => ({
      role: displayRole,
      permissions,
      email,
      emailValidated,
      hasListing,
      loading,
      refresh,
      can,
    }),
    [
      displayRole,
      permissions,
      email,
      emailValidated,
      hasListing,
      loading,
      refresh,
      can,
    ]
  )

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  )
}

export const usePermissions = () => {
  const ctx = useContext(PermissionsContext)
  if (!ctx) throw new Error('usePermissions must be used within PermissionsProvider')
  return ctx
}
